import { Fragment, lazy, memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { bridgeAtom, cartAtom, sessionAtom, useSetValue, useValue } from '@/store';

import { AppEvents } from '@/utils';

import { addShopifyEvent, getShopifyCart } from '@/modules/api';
import { useEvent } from '@/modules/common';
import { EventType, ProjectNameType, ShopifyCart } from '@/modules/models';

const Ahava = lazy(() => import('./components/ahava').then((module) => ({ default: module.Ahava })));
const Nununu = lazy(() => import('./components/nununu').then((module) => ({ default: module.Nununu })));

export const Cart = memo(
  () => {
    const session = useValue(sessionAtom);
    const { projectName } = useValue(bridgeAtom);

    const setCart = useSetValue(cartAtom);

    const prevProductsRef = useRef<ShopifyCart[]>([]);

    const sendCartEvent = useCallback(
      async ({ productId, variantId, quantity }: { productId: string; variantId?: string; quantity: number }) => {
        if (!session) {
          return;
        }

        try {
          await addShopifyEvent({
            type: EventType.ADD_TO_CART,
            userSession: session,
            productId,
            quantity,
            variantId,
          });
        } catch (error) {
          console.log(error);
        }
      },
      [session]
    );

    const cartDiffCheck = useCallback(
      async (currentProducts: ShopifyCart[], prevProducts: ShopifyCart[] = []) => {
        await Promise.all([
          ...currentProducts.map((product) => {
            const prevProduct = prevProducts?.find(({ handle }) => handle === product.handle);

            // new product added
            if (!prevProduct) {
              return sendCartEvent({
                productId: product.handle,
                quantity: product.quantity,
              });
            }

            // updated quantity of existing product
            if (prevProduct.quantity !== product.quantity) {
              return sendCartEvent({
                productId: product.handle,
                quantity: product.quantity,
              });
            }

            return Promise.resolve();
          }),
          ...prevProducts?.map((prevProduct) => {
            const isRemoved = !currentProducts.some(({ handle }) => handle === prevProduct.handle);

            if (isRemoved) {
              return sendCartEvent({
                productId: prevProduct?.handle,
                quantity: 0,
              });
            }

            return Promise.resolve();
          }),
        ]);
      },
      [sendCartEvent]
    );

    const getCart = useCallback(
      async (shouldSyncShopify = true) => {
        try {
          const cart = await getShopifyCart();

          const results = cart?.items.reduce<ShopifyCart[]>((acc, item) => {
            if (item?.properties?.['_is_gift']) {
              return acc;
            }

            const existingItem = acc.find((i) => i.handle === item.handle);

            if (existingItem) {
              existingItem.quantity += item.quantity;
            } else {
              acc.push({ ...item });
            }

            return acc;
          }, []);

          if (results) {
            setCart((s) => {
              prevProductsRef.current = s.products;
              return { ...s, products: results };
            });
          }
          if (shouldSyncShopify) {
            cartDiffCheck(results ?? [], prevProductsRef.current);
          }
        } catch (error) {
          console.log(error);
        }
      },
      [cartDiffCheck, setCart]
    );

    useEffect(() => {
      getCart(false);
    }, [getCart]);

    const onSetCart = useCallback(
      (event: CustomEvent<boolean>) => {
        setCart((s) => ({ ...s, isOpen: event.detail }));
      },
      [setCart]
    );

    useEvent({ name: AppEvents.TOGGLE_CART, handler: onSetCart });
    useEvent({ name: AppEvents.UPDATE_PRODUCT_LIST, handler: () => getCart(true) });

    // TODO: Refactor this to use ApiApp
    const Component = useMemo(() => {
      switch (projectName) {
        case ProjectNameType.AHAVA:
          return Ahava;
        case ProjectNameType.NUNUNU:
          return Nununu;
        default:
          return Fragment;
      }
    }, [projectName]);

    return <Component getCart={getCart} />;
  },
  () => true
);

Cart.displayName = 'Cart';
